import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

// --- Animations ---
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// --- Styled Components ---

const AppContainer = styled.div`
  font-family: Arial, sans-serif;
  max-width: 600px;
  margin: 80px auto;
  padding: 16px;
  text-align: center;
`;

const ReasoningContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
  background-color: #fafafa;
`;

const ReasoningLine = styled.div`
  margin: 8px 0;
  font-size: 1.1rem;
  line-height: 1.4;
  animation: ${fadeInUp} 0.4s ease forwards;
`;

const LoadingIndicator = styled.div`
  display: inline-block;
  margin-top: 16px;
  width: 24px;
  height: 24px;
  border: 3px solid #bbb;
  border-top: 3px solid #888;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const ReasoningViewerContainer = styled.div`
  position: relative;
  min-height: 40px; /* ensures space for loading spinner or text */
`;

const ReasoningDots = styled.div`
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
  &::after {
    content: '';
    animation: dots 1s steps(5, end) infinite;
  }

  @keyframes dots {
    0%, 20% {
      content: '';
    }
    40% {
      content: '.';
    }
    60% {
      content: '..';
    }
    80%, 100% {
      content: '...';
    }
  }
`;

// --- Main Components ---

/**
 * ReasoningViewer
 * Receives an array of reasoning steps and whether it's currently loading the next step.
 */
function ReasoningViewer({ reasoning, loading }) {
  return (
    <ReasoningContainer>
      {/* Display the latest reasoning steps (or you could show them all). */}
      <ReasoningViewerContainer>
        {reasoning.map((step) => (
          <ReasoningLine key={step.id}>{step.text}</ReasoningLine>
        ))}
        {/* Show a slick loading indicator if we're waiting for the next step */}
        {loading && (
          <>
            {/* Option 1: A rotating spinner */}
            {/* <LoadingIndicator /> */}
            
            {/* Option 2: Pulsing dots to show "thinking in progress" */}
            <ReasoningDots />
          </>
        )}
      </ReasoningViewerContainer>
    </ReasoningContainer>
  );
}

/**
 * DemoReasoningApp
 * Simulates receiving new reasoning steps every few seconds.
 */
export default function DemoReasoningApp() {
  const [reasoning, setReasoning] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Simulate reasoning updates
    const steps = [
      { id: 1, text: "Thinking about user question..." },
      { id: 2, text: "Gathering relevant data..." },
      { id: 3, text: "Analyzing the data for insights..." },
      { id: 4, text: "Formulating response..." },
      { id: 5, text: "Finalizing answer." },
      { id: 6, text: "Here is the final message to the user!" },
    ];

    let stepIndex = 0;

    function showNextStep() {
      // Start loading
      setLoading(true);

      // After a short delay, add the next step
      setTimeout(() => {
        if (stepIndex < steps.length) {
          setReasoning((prev) => [...prev, steps[stepIndex]]);
          stepIndex += 1;
        }

        // Stop loading after new step is added
        setLoading(false);

        // If there are still more steps, queue the next
        if (stepIndex < steps.length) {
          setTimeout(showNextStep, 3000);
        }
      }, 1500);
    }

    // Begin the chain of reasoning
    showNextStep();
  }, []);

  return (
    <AppContainer>
      <h1>Reasoning Steps Demo</h1>
      <p>This simulates a chatbot reasoning process, updating every few seconds.</p>
      <ReasoningViewer reasoning={reasoning} loading={loading} />
    </AppContainer>
  );
}

